'use client'

import { COOKIE_CONSENT_COUNTRIES } from '@/app/constants'
import useIdleLoading from '@/app/hooks/useIdleLoading'
import { useUserData } from '@/app/hooks/useUserData'
import dynamic from 'next/dynamic'
import Script from 'next/script'
import { FC, memo, useEffect, useRef, useState } from 'react'

declare global {
	interface Window {
		clarity?: (...args: any[]) => void
	}
}

interface Props {
	pageName: string
	siteLanguage?: string
}

export const RenderClarityBase: FC<Props> = ({ pageName, siteLanguage }) => {
	const clarityId = process.env.NEXT_PUBLIC_CLARITY_ID
	const isIdle = useIdleLoading()
	const { userData } = useUserData()
	const [isClarityReady, setIsClarityReady] = useState(false)
	const hasIdentified = useRef(false)

	// Poll until the Clarity command queue exists (the inline snippet defines it
	// asynchronously), mirroring RenderGA's gtag readiness check.
	useEffect(() => {
		if (!clarityId || !isIdle || hasIdentified.current) return

		const check = () => {
			if (typeof window !== 'undefined' && typeof window.clarity !== 'undefined') {
				setIsClarityReady(true)
			} else {
				setTimeout(check, 100)
			}
		}

		check()
	}, [clarityId, isIdle])

	useEffect(() => {
		if (!isClarityReady || hasIdentified.current) return

		const sessionID = userData?.sessionID
		if (!sessionID || typeof window.clarity === 'undefined') return

		// Flip the guard synchronously so a userData reference change cannot
		// re-run identify (matches RenderGA's hasLoadedGA discipline).
		hasIdentified.current = true

		// The stitch: same persistent sessionID GA uses, so all page loads across
		// the separate route-group layouts correlate into one session recording.
		window.clarity('identify', sessionID)
		window.clarity('set', 'page_name', pageName)
		if (siteLanguage) {
			window.clarity('set', 'site_language', siteLanguage)
		}

		// Consent mirrors GA. Regulated regions are governed by the Clarity
		// dashboard Consent Mode integration + Cookiebot, so we do NOT grant
		// explicitly there; everywhere else we grant cookie consent. identify
		// above already ran, so recordings stitch even in cookieless mode.
		const regulated =
			!!userData?.userCountry &&
			COOKIE_CONSENT_COUNTRIES.includes(userData.userCountry)
		if (!regulated) {
			window.clarity('consent')
		}
	}, [isClarityReady, userData, pageName, siteLanguage])

	if (!clarityId || !isIdle) return null

	return (
		<Script id="ms-clarity" strategy="afterInteractive">
			{`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityId}");`}
		</Script>
	)
}

export default dynamic(() => Promise.resolve(memo(RenderClarityBase)), {
	ssr: false,
})
